import React, {Fragment, useEffect} from 'react'
import {CreditCardIcon, NewspaperIcon, XCircleIcon, XMarkIcon} from '@heroicons/react/20/solid'
import {useNavigate} from "react-router-dom";
import {getVenueSession} from "../../requests/AppSession";
import dateFormat from "dateformat";
import {NumericFormat} from "react-number-format";
import {Transition} from "@headlessui/react";
import {useAuth0} from "@auth0/auth0-react";
import {CardGridSkeleton} from "./VTSkeletons";
import {createInvoicePaymentUrl} from "../requests/AppRequestStripeInvoicePayment";
import {findVenueInvoices} from "../requests/AppRequestVenueInvoice";


export default function VTAdminInvoices() {
    let navigate = useNavigate();

    const {isLoading, getAccessTokenSilently} = useAuth0();

    const [venueId, setVenueId] = React.useState(null);
    const [invoices, setInvoices] = React.useState(null);
    const [showAllInvoices, setShowAllInvoices] = React.useState(false);

    const [venueDashboardErrorMessage, setVenueDashboardErrorMessage] = React.useState(null);

    const [showError, setShowError] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const createPaymentUrlSuccess = (responseJson) => {
        setIsSubmitting(false);
        if (responseJson && responseJson.paymentUrl) {
            window.location.replace(responseJson.paymentUrl);
        }
    };

    const createPaymentUrlError = () => {
        setIsSubmitting(false);
        setShowError(true);
    };

    useEffect(() => {
        getVenueSession().then(venueSession => {
            if (venueSession && venueSession.venueId && venueSession.venueId !== null) {
                setVenueId(venueSession.venueId);
            } else {
                navigate('/login/mobile', {replace: true});
            }
        })
        return function cleanup() {

        };
    }, []);

    const getSuccess = async responseJson => {
        if (
            responseJson &&
            responseJson.id
        ) {
            setInvoices(responseJson.venueInvoiceDtoList);
        } else {
            setVenueDashboardErrorMessage("Error getting Venue Invoice information, please refresh and verify Internet Connection.");
        }
    };

    const getError = forceLogout => {
        setVenueDashboardErrorMessage("Error getting Venue Invoice information, please refresh and verify Internet Connection.");
    };

    useEffect(() => {
        if (!isLoading && venueId !== null) {
            findVenueInvoices(venueId, getSuccess, getError, getAccessTokenSilently);
        }
        return function cleanup() {

        };
    }, [isLoading, venueId]);

    return (
        <>
            <main>
                <div className="space-y-16 py-16 xl:space-y-20">
                    {/* Recent client list*/}
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
                            <div className="flex items-center justify-between">
                                {showAllInvoices ?
                                    <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">Unpaid & Paid
                                        Invoices</h2> :
                                    <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-gray-100">Unpaid
                                        Invoices</h2>}
                                {showAllInvoices ? <button
                                    className="text-sm font-semibold leading-6 text-pink-600 hover:text-pink-500"
                                    onClick={() => {
                                        setShowAllInvoices(false);
                                    }}
                                >
                                    View Unpaid Only
                                </button> : <button
                                    className="text-sm font-semibold leading-6 text-pink-600 hover:text-pink-500"
                                    onClick={() => {
                                        setShowAllInvoices(true);
                                    }}
                                >
                                    View Unpaid & Paid
                                </button>}
                            </div>
                            {!invoices && !venueDashboardErrorMessage && <CardGridSkeleton/>}
                            <ul role="list" className="mt-6 grid grid-cols-1 gap-x-6 gap-y-8 lg:grid-cols-3 xl:gap-x-8">
                                {venueDashboardErrorMessage && <div className="rounded-md bg-red-50 p-4 my-2">
                                    <div className="flex">
                                        <div className="flex-shrink-0">
                                            <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true"/>
                                        </div>
                                        <div className="ml-3">
                                            <p className="text-sm font-medium text-red-800">{venueDashboardErrorMessage}</p>
                                        </div>
                                        <div className="ml-auto pl-3">

                                        </div>
                                    </div>
                                </div>}

                                {invoices && invoices.map((invoice) => (
                                    invoice.paid && !showAllInvoices ? <></> : <li key={invoice.id}
                                                                                   className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                                        <dl className="-my-3 divide-y divide-gray-100 dark:divide-gray-700 px-6 py-4 text-sm leading-6">
                                            <div className="flex justify-between gap-x-4 py-3">
                                                <dt className="text-gray-500 dark:text-gray-400">Due By</dt>
                                                <dd className="text-gray-700 dark:text-gray-200">
                                                    {dateFormat(invoice.payBy, "mmmm dS, yyyy")}
                                                </dd>
                                            </div>
                                            <div className="flex justify-between gap-x-4 py-3">
                                                <dt className="text-gray-500 dark:text-gray-400">Amount Owed</dt>
                                                <dd className="flex items-start gap-x-2">
                                                    <div
                                                        className="font-medium text-gray-900 dark:text-gray-100">{<NumericFormat
                                                        value={invoice.totalAmount} displayType={'text'}
                                                        decimalScale={2} fixedDecimalScale={true}
                                                        thousandSeparator={true} prefix={'$'}/>}</div>
                                                    {invoice.paid ? <div
                                                            className={'text-green-700 bg-green-50 ring-green-600/20 rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset'}
                                                        >
                                                            Paid
                                                        </div> :
                                                        <div
                                                            className={'text-red-700 bg-red-50 ring-red-600/10 rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset'}
                                                        >
                                                            Due
                                                        </div>
                                                    }
                                                </dd>
                                            </div>
                                            <div className="flex justify-between gap-x-4 py-3">

                                                {invoice.paid ?
                                                    <div
                                                        className={'text-green-700 bg-green-50 ring-green-600/20 rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset'}
                                                    >
                                                        Paid on {dateFormat(invoice.paidDate, "mmmm dS, yyyy")}
                                                    </div>
                                                    : <button
                                                        type="button"
                                                        disabled={isSubmitting}
                                                        className="inline-flex items-center gap-x-1.5 rounded-md bg-green-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
                                                        onClick={() => {
                                                            setIsSubmitting(true);
                                                            createInvoicePaymentUrl(venueId, invoice.id, createPaymentUrlSuccess, createPaymentUrlError, getAccessTokenSilently);
                                                        }}
                                                    >
                                                        Pay Invoice
                                                        <CreditCardIcon className="-mr-0.5 h-5 w-5" aria-hidden="true"/>
                                                    </button>}
                                                <dd className="flex items-start gap-x-2">
                                                    <button
                                                        type="button"
                                                        className="inline-flex items-center gap-x-1.5 rounded-md bg-pink-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-pink-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600"
                                                        onClick={() => {
                                                            navigate("/venue/invoice/details", {state: {invoiceId: invoice.id}});

                                                        }}
                                                    >
                                                        View Details
                                                        <NewspaperIcon className="-mr-0.5 h-5 w-5" aria-hidden="true"/>
                                                    </button>
                                                </dd>
                                            </div>
                                        </dl>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </main>

            {/* Global notification live region, render this permanently at the end of the document */}
            <div
                aria-live="assertive"
                className="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6"
            >
                <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
                    {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
                    <Transition
                        show={showError}
                        as={Fragment}
                        enter="transform ease-out duration-300 transition"
                        enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
                        enterTo="translate-y-0 opacity-100 sm:translate-x-0"
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div
                            className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5">
                            <div className="p-4">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                        <XCircleIcon className="h-6 w-6 text-red-700" aria-hidden="true"/>
                                    </div>
                                    <div className="ml-3 w-0 flex-1 pt-0.5">
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Paying Invoice failed, please
                                            try again!</p>
                                    </div>
                                    <div className="ml-4 flex flex-shrink-0">
                                        <button
                                            type="button"
                                            className="inline-flex rounded-md bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-green-700 focus:ring-offset-2"
                                            onClick={() => {
                                                setShowError(false)
                                            }}
                                        >
                                            <span className="sr-only">Close</span>
                                            <XMarkIcon className="h-5 w-5" aria-hidden="true"/>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Transition>
                </div>
            </div>
        </>
    )
}
